import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

// ── セル内画像のホバー拡大プレビュー ──────────────────────────────
// セル内ではサムネイル（最大 120px）に縮めているため、ホバー中だけ
// 元画像を大きく浮かせて確認できるようにする（クリック不要・追従なし）。
// タスクボードのセル（TaskCell）と、スケジュールの行ホバーカード
// （GanttTaskHoverCard）で共用する。

/** 拡大プレビューの最大幅（ビューポートの空きが狭ければさらに縮む） */
const PREVIEW_MAX_WIDTH_PX = 640;
/** 拡大プレビューの最大高さ（ビューポート高さに対する比率） */
const PREVIEW_MAX_HEIGHT_RATIO = 0.7;
/** プレビューとサムネイル・画面端との余白 */
const PREVIEW_MARGIN_PX = 12;

/**
 * 拡大プレビュー本体（body 直下へポータル表示）。
 * ホバー補助の表示なので操作は受け付けない（pointer-events: none）。
 * z-index はモーダル（1000）より下、表の sticky 列とホバーカード（HoverCard
 * の 950）より上。
 */
const HoverImagePreviewBox = styled.div`
  position: fixed;
  z-index: 960;
  pointer-events: none;

  & img {
    display: block;
    border-radius: 8px;
    border: 1px solid var(--soremo-border);
    background: #fff;
    box-shadow: 2px 4px 16px 4px rgba(0, 0, 0, 0.2);
  }
`;

/** ホバー中のサムネイルの位置（getBoundingClientRect のスナップショット） */
interface PreviewAnchor {
  top: number;
  left: number;
  right: number;
}

/**
 * サムネイル画像のホバー拡大プレビュー。
 * ツールチップと同じく対象のすぐ横（左右で空きが広い側）に出し、
 * 上端をサムネイルに揃える。実際の描画サイズは画像を読み込むまで
 * 分からないため、読み込み完了までは不可視にし、確定後に
 * 「画面からはみ出す分だけ上へずらす」クランプを掛けてから表示する。
 */
const ImageHoverPreview: React.FC<{ url: string; anchor: PreviewAnchor }> = ({
  url,
  anchor,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  // 縦位置。null = 実寸が未確定（読み込み待ち。プレビューは不可視）
  const [top, setTop] = useState<number | null>(null);

  const vw = window.innerWidth;
  const spaceRight = vw - anchor.right - PREVIEW_MARGIN_PX * 2;
  const spaceLeft = anchor.left - PREVIEW_MARGIN_PX * 2;
  const useRight = spaceRight >= spaceLeft;
  const maxWidth = Math.max(
    Math.min(PREVIEW_MAX_WIDTH_PX, useRight ? spaceRight : spaceLeft),
    120,
  );
  const maxHeight = Math.round(
    window.innerHeight * PREVIEW_MAX_HEIGHT_RATIO,
  );

  /** 実寸確定後の縦位置決め。サムネイル上端に揃え、画面内へクランプする */
  const place = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    const height = img.getBoundingClientRect().height;
    setTop(
      Math.max(
        PREVIEW_MARGIN_PX,
        Math.min(anchor.top, window.innerHeight - height - PREVIEW_MARGIN_PX),
      ),
    );
  }, [anchor.top]);

  // キャッシュ済み画像は onLoad が発火しないことがあるため、マウント時に
  // 読み込み済み（complete）なら即座に位置決めする。
  useLayoutEffect(() => {
    if (imgRef.current?.complete) place();
  }, [place]);

  return createPortal(
    <HoverImagePreviewBox
      aria-hidden="true"
      style={{
        top: top ?? anchor.top,
        visibility: top === null ? "hidden" : "visible",
        ...(useRight
          ? { left: anchor.right + PREVIEW_MARGIN_PX }
          : { right: vw - anchor.left + PREVIEW_MARGIN_PX }),
      }}
    >
      <img
        ref={imgRef}
        src={url}
        alt=""
        style={{ maxWidth, maxHeight }}
        onLoad={place}
      />
    </HoverImagePreviewBox>,
    document.body,
  );
};

/**
 * 中に描画された画像（Markdown 本文の `<img>` 等）の上に乗ったら拡大
 * プレビューを出し、離れたら消す領域。Markdown レンダリング内の `<img>` に
 * 直接 props を渡せないため、mouseover / mouseout のバブリングで拾う。
 * 表示中にスクロール・リサイズしたらプレビューを閉じる（fixed 配置のため
 * サムネイルの移動に追従できず、位置がずれる）。
 */
const ImageHoverZone: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [preview, setPreview] = useState<{
    url: string;
    anchor: PreviewAnchor;
  } | null>(null);

  const handleMouseOver = (e: React.MouseEvent) => {
    const img = (e.target as HTMLElement).closest("img");
    if (!(img instanceof HTMLImageElement) || !img.src) return;
    const rect = img.getBoundingClientRect();
    setPreview({
      url: img.src,
      anchor: { top: rect.top, left: rect.left, right: rect.right },
    });
  };

  const handleMouseOut = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("img")) setPreview(null);
  };

  useEffect(() => {
    if (!preview) return;
    const close = () => setPreview(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [preview]);

  return (
    <div onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
      {children}
      {preview && <ImageHoverPreview url={preview.url} anchor={preview.anchor} />}
    </div>
  );
};

export default ImageHoverZone;
